/**
 * Customer discount overview -- phase 6b, Task 4.
 *
 * `GET /admin/discounts/customers/:id` -- the one-call profile: identity,
 * loyalty standing (card, balance, tier, rewards), referral standing (code,
 * conversions, rewards) and the latest 20 redemptions (ADMIN_API.md
 * "Redemptions & customers"). 404 -> "No such customer" (any other error ->
 * the mapped `message`).
 *
 * `loyalty.tierNumber === null` means the tier bands are misconfigured, not
 * "new customer" (ruling A-1: tier 1 starts at spend 0, so every contiguous,
 * active band set covers every customer). `loyalty.rewards` INCLUDES
 * inactive rewards (`active: false`) deliberately -- rendered visibly muted
 * rather than filtered out, so an admin can see a reward they just switched
 * off.
 *
 * Owns the "Adjust points" control and the dialog's open state -- gated
 * behind `useCanWriteDiscounts()` -- because it is the one place that both
 * knows the current balance (for the dialog's preview) and can show the
 * post-adjustment banner next to the data it just changed.
 */
import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { discountsApi, qk } from '../api';
import { toApiFailure } from '../errors';
import { formatRand } from '../money';
import { useCanWriteDiscounts } from '../roles';
import type { RewardRow } from '../types';
import { PointsAdjustmentDialog, type AdjustmentSuccess } from './PointsAdjustmentDialog';
import { LedgerTable } from './LedgerTable';

const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const RewardsList: React.FC<{ rewards: RewardRow[]; emptyLabel: string }> = ({ rewards, emptyLabel }) => {
  if (rewards.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyLabel}
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {rewards.map((r) => (
        <Chip
          key={r.code}
          label={`${r.code} · ${formatRand(r.amount)}${r.timesRedeemed > 0 ? ' · used' : ''}`}
          size="small"
          variant={r.active ? 'filled' : 'outlined'}
          // Inactive rewards stay in the list (per the API's rule above) but
          // read as greyed-out / struck-through, never removed.
          sx={r.active ? undefined : { opacity: 0.5, textDecoration: 'line-through' }}
          title={`Issued ${formatDate(r.issuedAt)}${r.active ? '' : ' · inactive'}`}
        />
      ))}
    </Box>
  );
};

export const CustomerOverview: React.FC<{ id: string }> = ({ id }) => {
  const canWrite = useCanWriteDiscounts();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [lastResult, setLastResult] = useState<AdjustmentSuccess | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: qk.customer(id),
    queryFn: () => discountsApi.customerOverview(id),
  });

  if (isLoading) {
    return <Skeleton height={140} sx={{ mb: 3 }} />;
  }

  if (error) {
    const failure = toApiFailure(error);
    if (failure.status === 404) {
      return (
        <Alert severity="warning" sx={{ mb: 3 }}>
          No such customer.
        </Alert>
      );
    }
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {failure.message}
      </Alert>
    );
  }

  if (!data) return null;

  const { user, loyalty, referral, redemptions } = data;

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {user.firstName} {user.lastName ?? ''}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user.email}
            {user.phoneNumber ? ` · ${user.phoneNumber}` : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Joined {formatDate(user.createdAt)}
          </Typography>
        </Box>
        {canWrite && (
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setLastResult(null);
              setDialogOpen(true);
            }}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Adjust points
          </Button>
        )}
      </Box>

      {lastResult && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setLastResult(null)}>
          Balance {lastResult.balanceBefore} → {lastResult.balanceAfter}
          {lastResult.rewardsIssued.length > 0 && ` · reward issued: ${lastResult.rewardsIssued.join(', ')}`}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 2, mb: 3 }}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Loyalty
            </Typography>
            {loyalty.cardId ? (
              <>
                <Typography variant="body2">Card {loyalty.cardNumber}</Typography>
                <Typography variant="body2">
                  Balance: <strong>{loyalty.pointsBalance}</strong> pts
                </Typography>
                <Typography variant="body2">Lifetime earned: {loyalty.pointsEarnedLifetime} pts</Typography>
                <Typography variant="body2">Lifetime spend: {formatRand(loyalty.lifetimeSpend)}</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Tier: {loyalty.tierNumber == null ? 'No tier (check the band configuration)' : loyalty.tierNumber}
                </Typography>
                <RewardsList rewards={loyalty.rewards} emptyLabel="No rewards issued yet." />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No loyalty card yet.
              </Typography>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Referral
            </Typography>
            <Typography variant="body2">Code: {referral.code ?? '—'}</Typography>
            <Typography variant="body2">Converted: {referral.converted}</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Reverted: {referral.reverted}
            </Typography>
            <RewardsList rewards={referral.rewards} emptyLabel="No referral rewards yet." />
          </CardContent>
        </Card>
      </Box>

      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        Latest redemptions
      </Typography>
      {redemptions.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No redemptions yet.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Promotion</TableCell>
              <TableCell>Voucher</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Order</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {redemptions.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{r.promotion.name}</TableCell>
                <TableCell>{r.voucher.code}</TableCell>
                <TableCell>{r.status}</TableCell>
                <TableCell align="right">{formatRand(r.amount)}</TableCell>
                <TableCell>{r.order.number ?? r.order.id}</TableCell>
                <TableCell>{formatDate(r.createdAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {canWrite && (
        <PointsAdjustmentDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          customerId={id}
          currentBalance={loyalty.pointsBalance}
          onSuccess={(result) => {
            setLastResult(result);
            // The whole ['discounts'] prefix, not just this customer + ledger:
            // an adjustment can cross a tier boundary and auto-issue a reward
            // code, which moves the stats bar, the tier promotion's voucher
            // counts and its codes list too (fix wave minor 5).
            queryClient.invalidateQueries({ queryKey: qk.all });
          }}
        />
      )}
    </Box>
  );
};

export default CustomerOverview;

/**
 * `/discounts/customers/:id` -- the route wrapper: overview card(s) above
 * the full ledger drill-down. Lives here (rather than in `index.ts`, a
 * plain `.ts` re-export file that cannot hold JSX) and is re-exported by
 * `./index`.
 */
export const CustomersDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  if (!id) {
    return (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        No customer selected.
      </Typography>
    );
  }

  return (
    <Box>
      <CustomerOverview id={id} />
      <LedgerTable customerId={id} />
    </Box>
  );
};
